import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem, Button, useMediaQuery } from '@mui/material';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useAuth from 'src/hooks/useAuth';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import { useDispatch } from 'src/redux/hooks';
import MyAvatar from 'src/components/MyAvatar';
import MenuPopover from 'src/components/MenuPopover';
import { IconButtonAnimate } from 'src/components/animate';
import { useOpenApplications } from 'src/portals/customer-portal/hooks/application-hooks';
import { createNewApplication, setEditApplication } from 'src/redux/slices/editApplication';
import { onUserLogout } from 'src/redux/slices/mainApp';
import useCurrentUser from 'src/hooks/useCurrentUser';
import { getMaxCustomerApplications } from 'src/portals/components/application-flow/application-utils';
import { GetDefaultNewApplication } from 'src/utils/applicationUtils';
import { RBF_PRODUCT_ID, TERMLOAN_PRODUCT_ID } from 'src/@types/application-types';
import useFeatureFlags from '../../../../hooks/useFeatureFlags';

type MenuOption = {
  label: string;
  linkTo: string;
};

const MENU_OPTIONS = [
  {
    label: 'Profile',
    linkTo: PATH_DASHBOARD.user.profile,
  },
  {
    label: 'Account',
    linkTo: PATH_DASHBOARD.user.account,
  },
] as MenuOption[];

type AccountPopoverProps = {
  setIsContactSupportVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function AccountPopover({ setIsContactSupportVisible }: AccountPopoverProps) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout } = useAuth();
  const user = useCurrentUser();
  const isMountedRef = useIsMountedRef();
  const { isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const openApplications = useOpenApplications();
  const [open, setMenuOpen] = useState<HTMLElement | null>(null);
  const featureFlags = useFeatureFlags();
  const allowTermLoans = featureFlags.isFeatureEnabled(featureFlags.featureFlagKeys.allowTermLoans);

  // Access user roles
  const userRoles = user.roles || [];
  const hasISORole = userRoles.includes('iso');

  const isISORoute = React.useMemo(() => pathname.includes('isoapp'), [pathname]);
  const isSupportRoute = React.useMemo(() => pathname.includes('support'), [pathname]);
  const maxApplications = getMaxCustomerApplications(user.isLibertasUser);

  const rbfOpenApplications = React.useMemo(
    () =>
      openApplications ? openApplications.filter((app) => app.productId === RBF_PRODUCT_ID) : [],
    [openApplications]
  );

  const tlOpenApplications = React.useMemo(
    () =>
      openApplications
        ? openApplications.filter((app) => app.productId === TERMLOAN_PRODUCT_ID)
        : [],
    [openApplications]
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuOpen(event.currentTarget);
  };

  const handleClose = () => {
    setMenuOpen(null);
  };
  const handleOpenContactSupport = () => {
    setIsContactSupportVisible(true);
    setMenuOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      dispatch(onUserLogout());

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleApplyNow = (productId: string) => {
    const application = GetDefaultNewApplication();

    dispatch(
      createNewApplication({
        application: {
          ...application,
          productId,
          applicant: {
            ...application.applicant,
            basicInfoEdited: false,
          },
        },
      })
    );
  };

  const canEditApplicationRBF = React.useMemo(
    () => rbfOpenApplications.some((application) => application.status === 'NotSubmitted'),
    [rbfOpenApplications]
  );

  const canEditApplicationTL = React.useMemo(
    () => tlOpenApplications.some((application) => application.status === 'NotSubmitted'),
    [tlOpenApplications]
  );

  const handleEditRBFApplication = () => {
    if (rbfOpenApplications.length === 0) return;

    dispatch(setEditApplication(rbfOpenApplications[0]));
    navigate(`${PATH_DASHBOARD.app.applicationWizard}?appId=${rbfOpenApplications[0].appId}`);
  };

  const handleEditTermLoanApplication = () => {
    if (tlOpenApplications.length === 0) return;

    dispatch(setEditApplication(tlOpenApplications[0]));
    navigate(`${PATH_DASHBOARD.app.applicationWizard}?appId=${tlOpenApplications[0].appId}`);
  };

  return (
    <>
      {isAuthenticated && !hasISORole && !isISORoute && !isSupportRoute && canEditApplicationRBF ? (
        <Button variant={'contained'} onClick={handleEditRBFApplication}>
          {isMobile ? 'Continue App' : 'Continue RBF Application'}
        </Button>
      ) : !hasISORole && !(isISORoute || isSupportRoute) ? (
        <Button
          variant={'contained'}
          to="./mpapp/application/wizard"
          onClick={() => handleApplyNow(RBF_PRODUCT_ID)}
          component={RouterLink}
          disabled={rbfOpenApplications.length >= maxApplications}
        >
          Apply Now RBF
        </Button>
      ) : null}
      {isAuthenticated && !hasISORole && !isISORoute && !isSupportRoute && canEditApplicationTL ? (
        <Button variant={'contained'} onClick={handleEditTermLoanApplication}>
          Continue Term Loan Application
        </Button>
      ) : !(isISORoute || isSupportRoute) && allowTermLoans ? (
        <Button
          variant={'contained'}
          to="./mpapp/application/wizard"
          onClick={() => handleApplyNow(TERMLOAN_PRODUCT_ID)}
          component={RouterLink}
          disabled={tlOpenApplications.length >= maxApplications}
        >
          Apply Now Term Loan
        </Button>
      ) : null}
      <Box sx={{ my: 0.5, px: 2.5, display: { xs: 'none', md: 'block' } }}>
        <Typography
          variant="caption"
          align={'right'}
          noWrap
          sx={{ color: theme.palette.primary.main, mb: 0 }}
        >
          Logged in as
        </Typography>
        <Typography variant="body2" align={'right'} sx={{ color: 'rgb(30, 30, 30)', mt: 0 }} noWrap>
          {`${user?.firstName} ${user?.lastName}`}
        </Typography>
      </Box>

      <IconButtonAnimate
        aria-label="Open"
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[600], 0.2),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        {/*<Box sx={{ my: 1.5, px: 2.5 }}>*/}
        {/*  <Typography variant="subtitle2" noWrap>*/}
        {/*    {user?.displayName}*/}
        {/*  </Typography>*/}
        {/*  <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>*/}
        {/*    {user?.email}*/}
        {/*  </Typography>*/}
        {/*</Box>*/}

        {/*<Divider sx={{ borderStyle: 'dashed' }} />*/}

        {MENU_OPTIONS.length > 0 && (
          <>
            <Stack sx={{ p: 1 }}>
              {MENU_OPTIONS.map((option) => (
                <MenuItem
                  key={option.label}
                  to={option.linkTo}
                  component={RouterLink}
                  onClick={handleClose}
                >
                  {option.label}
                </MenuItem>
              ))}

              <MenuItem onClick={handleOpenContactSupport}>{'Contact Support'}</MenuItem>
            </Stack>
            <Divider sx={{ borderStyle: 'solid' }} />
          </>
        )}

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
      </MenuPopover>
    </>
  );
}
